import g from '#core/global';


const load_type_now = 'now';
const load_type_style = 'style';
const load_type_content = 'content';
const load_type_asset = 'asset';

const state = window.teal_state = window.teal_state || {};
state.load = state.load || {};
state.load.module_init = state.load.module_init || [];
state.load.is_loaded = state.load.is_loaded || {
	[load_type_now]: true,
	[load_type_style]: false,
	[load_type_content]: false,
	[load_type_asset]: false,
};
state.load.callbacks = state.load.callbacks || {
	[load_type_now]: [],
	[load_type_style]: [],
	[load_type_content]: [],
	[load_type_asset]: [
		inject_scripts,
	],
};

const module_name = 'load';
const ready_state_e = 'readystatechange';
const content_loaded_e = 'interactive';
const asset_loaded_e = 'complete';

const allow = [
	// 'expandable',
];
const disallow = [
	'slideshow-swiper',
];


const module = {
	NOW: load_type_now,
	STYLE: load_type_style,
	CONTENT: load_type_content,
	ASSET: load_type_asset,
	
	
	/**
	 * Has this module already been initialized?
	 * @param {string} name
	 * @return {boolean}
	 */
	ran(name) {
		if (state.load.module_init.includes(name)) return true;
		
		if (
			allow.length
			&& !allow.includes(name)
			&& name !== module_name
		) return true;
		
		if (
			disallow.includes(name)
			&& name !== module_name
		) return true;
		
		state.load.module_init.push(name);
		return false;
	},
	
	
	/**
	 * Init module
	 */
	init() {
		if (this.ran(module_name)) return false;

		set_document_title();

		// Check if document is already in interactive or complete state
		if (
			document.readyState === content_loaded_e
			|| document.readyState === asset_loaded_e
		) {
			fire_on_dom_event(load_type_content);
		}
		if (document.readyState === asset_loaded_e) {
			fire_on_dom_event(load_type_asset);
		}
		
		// Track document ready state changes
		document.addEventListener(ready_state_e, e => {
			if (e.target.readyState === content_loaded_e) {
				fire_on_dom_event(load_type_content);
			} else if (e.target.readyState === asset_loaded_e) {
				fire_on_dom_event(load_type_asset);
			}
		});
		
		// Track CSS and font loading
		check_style_loading().then(() => {
			fire_on_dom_event(load_type_style);
		});
	},
	
	
	/**
	 * Fire immediately
	 * @param {function} callback
	 * @return {boolean}
	 */
	now(callback) {
		return fire(load_type_now, callback);
	},
	
	
	/**
	 * Fire when styles and fonts are loaded
	 * @param {function} callback
	 * @return {boolean}
	 */
	style(callback) {
		return fire(load_type_style, callback);
	},
	
	
	/**
	 * Fire when content is loaded
	 * @param {function} callback
	 * @return {boolean}
	 */
	content(callback) {
		return fire(load_type_content, callback);
	},
	
	
	/**
	 * Fire when all assets are loaded
	 * @param {function} callback
	 * @return {boolean}
	 */
	all(callback) {
		return fire(load_type_asset, callback);
	},
	
	
	/**
	 * Fire when multiple load types are satisfied
	 * @param {string[]} types
	 * @param {function} callback
	 * @return {boolean}
	 */
	when(types, callback) {
		if (!Array.isArray(types) || types.length === 0) return false;
		
		if (!callback || typeof callback !== 'function') {
			return types.every(type => state.load.is_loaded[type]);
		}
		
		const is_fully_loaded = types.every(type => state.load.is_loaded[type]);
		if (is_fully_loaded) {
			callback();
			return true;
		}
		
		let has_fired = false;
		
		types.forEach(type => {
			state.load.callbacks[type].push(() => {
				if (has_fired) return;
				
				const is_fully_loaded = types.every(type => state.load.is_loaded[type]);
				if (!is_fully_loaded) return;
				
				has_fired = true;
				callback();
			});
		});
		
		return false;
	},
	
};

export default module;
module.init();


/**
 * Fire
 * @param {string} load_type
 * @param {function} callback
 * @return {boolean}
 */
function fire(load_type, callback) {
	if (!callback || typeof callback !== 'function') {
		return state.load.is_loaded[load_type];
	}
	
	state.load.callbacks[load_type].push(callback);
	if (!state.load.is_loaded[load_type]) return false;
	
	callback();
	return true;
}


/**
 * Fire on DOM event
 * @param {string} load_type
 */
function fire_on_dom_event(load_type) {
	state.load.is_loaded[load_type] = true;
	state.load.callbacks[load_type].forEach(callback => {
		callback();
	});
}


/**
 * Wait for CSS and fonts to load
 * @return {Promise}
 */
function check_style_loading() {
	const promises = [];
	
	if (document.styleSheets.length > 0) {
		const $stylesheets = document.querySelectorAll('link[rel="stylesheet"]');
		const stylesheet_promises = Array.from($stylesheets).map($link => {
			return ($link.sheet)
				? Promise.resolve()
				: new Promise(resolve => $link.addEventListener(g.load_e, resolve, {
						once: true,
					}))
			;
		});
		promises.push(...stylesheet_promises);
	}
	
	if (document.fonts && document.fonts.ready) {
		promises.push(document.fonts.ready);
	}
	
	if (promises.length === 0) {
		return Promise.resolve();
	}
	
	return Promise.all(promises);
}


/**
 * Set the document title to the pattern name from the URL
 */
function set_document_title() {
	const params = new URLSearchParams(window.location.search);
	const pattern = params.get('pattern');
	if (!pattern || params.get('iframe') !== 'true') return;

	document.title = pattern.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}


/**
 * Inject third-party scripts
 * 
 * Uses:
 * - Load third-party scripts that aren't managed by WordPress
 * - Load scripts that should always load after all other assets are loaded
 */
function inject_scripts() {
	const scripts = [
		// '/script.js',
	];
	scripts.forEach(src => {
		const script = document.createElement('script');
		script.src = src;
		script.async = true;
		document.head.appendChild(script);
	});
}
